import React, { useEffect, useRef, useState } from 'react';
import ApexCharts, { ApexOptions } from 'apexcharts';
import { Filter } from './FilterYear';
import { getBalanceChart } from '../../../service/dashboard/Dashboard';

export interface GraficaBalanceProps {
  data?: {
    activationCurrent: number;
    activationPrevious: number;
    portabilityCurrent: number;
    portabilityPrevious: number;
    rechargeCurrent: number;
    rechargePrevious: number;
  };
}

interface FilterValues {
  comparisonType: 'month' | 'year';
  year_1?: string;
  year_2?: string;
  month1?: string;
  month2?: string;
}

const monthNames: Record<string, string> = {
  '1': 'Ene',
  '2': 'Feb',
  '3': 'Mar',
  '4': 'Abr',
  '5': 'May',
  '6': 'Jun',
  '7': 'Jul',
  '8': 'Ago',
  '9': 'Sep',
  '10': 'Oct',
  '11': 'Nov',
  '12': 'Dic',
};

const monthNamesFull: Record<string, string> = {
  '1': 'Enero',
  '2': 'Febrero',
  '3': 'Marzo',
  '4': 'Abril',
  '5': 'Mayo',
  '6': 'Junio',
  '7': 'Julio',
  '8': 'Agosto',
  '9': 'Septiembre',
  '10': 'Octubre',
  '11': 'Noviembre',
  '12': 'Diciembre',
};

const GraficaBalance: React.FC<GraficaBalanceProps> = ({ data }) => {
  const [seriesData, setSeriesData] = useState<any[]>([]);
  const [seriesLabels, setSeriesLabels] = useState<string[]>([
    'Actual',
    'Anterior',
  ]);
  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<ApexCharts | null>(null);
  const [filterValues, setFilterValues] = useState<FilterValues | null>(null);
  const [subtitleText, setSubtitleText] = useState(
    `${currentYear} vs ${previousYear}`,
  );
  const categories = ['Activaciones', 'Portabilidades', 'Recargas'];

  const handleFilter = (values: FilterValues) => {
    setFilterValues(values);
  };

  const handleFilterClean = () => {
    setFilterValues(null);
  };

  const mapApiDataToChart = (apiData: any, year_1: string, year_2: string) => {
    const data1 = apiData[year_1] || [];
    const data2 = apiData[year_2] || [];

    const getValue = (dataArray: any[], name: string) => {
      const item = dataArray.find((d: any) => d.name === name);
      return item?.amount || 0;
    };

    return [
      {
        name: year_1,
        data: [
          getValue(data1, 'Activaciones'),
          getValue(data1, 'Portabilidades'),
          getValue(data1, 'Recargas'),
        ],
      },
      {
        name: year_2,
        data: [
          getValue(data2, 'Activaciones'),
          getValue(data2, 'Portabilidades'),
          getValue(data2, 'Recargas'),
        ],
      },
    ];
  };

  useEffect(() => {
    const loadChartData = async () => {
      try {
        let params: Record<string, any>;
        let newSubtitle = '';
        let key1 = currentYear.toString();
        let key2 = previousYear.toString();
        let newLabels: string[] = [];

        if (filterValues) {
          const {
            comparisonType,
            year_1: y1,
            year_2: y2,
            month1,
            month2,
          } = filterValues;

          const year_1 = y1 || currentYear.toString();
          const year_2 = y2 || previousYear.toString();

          if (comparisonType === 'year') {
            params = {
              filter_type: 'year',
              year_1,
              year_2,
            };

            key1 = year_1;
            key2 = year_2;
            newSubtitle = `${year_1} vs ${year_2}`;
            newLabels = [year_1, year_2];
          } else {
            const m1 = month1 || '1';
            const m2 = month2 || '2';

            params = {
              filter_type: 'month',
              year_1,
              year_2,
              month_1: m1,
              month_2: m2,
            };

            key1 = `${monthNamesFull[m1]} ${year_1}`;
            key2 = `${monthNamesFull[m2]} ${year_2}`;
            newSubtitle = `${monthNames[m1]} ${year_1} vs ${monthNames[m2]} ${year_2}`;
            newLabels = [
              `${monthNames[m1]} ${year_1}`,
              `${monthNames[m2]} ${year_2}`,
            ];
          }
        } else {
          params = {
            filter_type: 'year',
            year_1: currentYear.toString(),
            year_2: previousYear.toString(),
          };
          key1 = currentYear.toString();
          key2 = previousYear.toString();
          newSubtitle = `${currentYear} vs ${previousYear}`;
          newLabels = [currentYear.toString(), previousYear.toString()];
        }

        const apiData = await getBalanceChart(params);

        const newSeriesData = mapApiDataToChart(apiData, key1, key2);

        setSeriesData(newSeriesData);
        setSeriesLabels(newLabels);
        setSubtitleText(newSubtitle);
      } catch (error) {
        console.error('Error loading chart data:', error);
        setSeriesData([]);
      }
    };

    loadChartData();
  }, [filterValues, currentYear, previousYear]);

  useEffect(() => {
    if (!chartRef.current || seriesData.length === 0) return;

    const height = parseInt(window.getComputedStyle(chartRef.current).height);

    if (!chartInstance.current) {
      chartInstance.current = new ApexCharts(
        chartRef.current,
        getChartOptions(height, seriesData, categories, seriesLabels),
      );
      chartInstance.current.render();
    } else {
      chartInstance.current.updateOptions(
        getChartOptions(height, seriesData, categories, seriesLabels),
        true,
        true,
      );
    }

    return () => {
      chartInstance.current?.destroy();
      chartInstance.current = null;
    };
  }, [seriesData, seriesLabels]);

  return (
    <div className="card">
      <div className="card-header border-0 pt-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bold fs-3 mb-1">
            Balance de operaciones: {subtitleText}
          </span>
          <span className="text-muted fw-semibold fs-7">
            Comparación de rendimiento entre periodos
          </span>
        </h3>
        <div className="card-toolbar">
          <Filter
            onCleanFilter={handleFilterClean}
            onSearchFilter={handleFilter}
          />
        </div>
      </div>

      <div className="card-body">
        <div>
          {seriesData.length === 0 ? (
            <div className="text-muted text-center py-5">
              Cargando gráfica...
            </div>
          ) : (
            <div
              ref={chartRef}
              id="kt_charts_widget_2_chart"
              style={{ height: '400px' }}
            ></div>
          )}
        </div>
      </div>
    </div>
  );
};

export { GraficaBalance };

function getChartOptions(
  height: number,
  series: any[],
  categories: string[],
  seriesLabels: string[],
): ApexOptions {
  const labelColor = '#6c757d';
  const borderColor = '#e9ecef';

  const colorsActual = ['#CFB1EC', '#f3dd8f', '#A0DDFD'];
  const colorsAnterior = ['#883DCF', '#FFBB00', '#2BB2FE'];

  return {
    series,
    chart: {
      fontFamily: 'inherit',
      type: 'bar',
      height,
      toolbar: { show: false },
      offsetY: -20, // Espacio superior para las etiquetas
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        borderRadius: 6,
        dataLabels: {
          position: 'top',
        },
      },
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: true,
      formatter: function (
        val: string | number | number[],
        opts: { seriesIndex: number; dataPointIndex: number },
      ) {
        const numVal =
          typeof val === 'number'
            ? val
            : Array.isArray(val)
            ? val[0]
            : parseFloat(val);
        const label = seriesLabels[opts.seriesIndex] || '';
        return [label, `$${numVal.toLocaleString('es-MX')}`];
      } as any,
      offsetY: -31,
      style: {
        fontSize: '10px',
        fontWeight: 'bold',
        colors: [
          function (opts: { seriesIndex: number; dataPointIndex: number }) {
            return opts.seriesIndex === 0
              ? colorsActual[opts.dataPointIndex]
              : colorsAnterior[opts.dataPointIndex];
          },
        ] as any,
      },
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: { colors: labelColor, fontSize: '13px', fontWeight: '600' },
      },
    },
    yaxis: {
      labels: {
        formatter: (val) =>
          val != null
            ? `$${val.toLocaleString('es-MX', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}`
            : '$0',
        style: { colors: labelColor, fontSize: '12px' },
      },
      max: (max: number) => Math.ceil(max * 1.25),
    },
    fill: {
      opacity: 1,
      colors: [
        function ({ dataPointIndex }: { dataPointIndex: number }) {
          return colorsActual[dataPointIndex];
        },
        function ({ dataPointIndex }: { dataPointIndex: number }) {
          return colorsAnterior[dataPointIndex];
        },
      ] as any,
    },
    states: {
      normal: { filter: { type: 'none', value: 0 } },
      hover: { filter: { type: 'lighten', value: 0.1 } },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: { type: 'none', value: 0 },
      },
    },
    tooltip: {
      style: { fontSize: '12px' },
      y: {
        formatter: (val) =>
          val != null
            ? `$${val.toLocaleString('es-MX', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`
            : '$0.00',
      },
    },
    grid: {
      borderColor,
      strokeDashArray: 4,
      yaxis: { lines: { show: true } },
    },
  };
}
